import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'qs';
import PageTitle from '@/components/PageTitle';
import { QuestionCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { Table, Button, Typography, DatePicker, Row, Col, Card, Statistic, Empty, Tooltip } from 'antd';
import moment from 'moment';
import { CardHelp, cleanObject, isValidDate } from '@/utils/helpers';
import { customerVisitStatistics } from '@/redux/actions/statisticsActions';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/customer-visits', breadcrumbName: 'Thống kê lượt khách đến' },
];

export default function CustomerVisits() {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const { summary, by_group } = useSelector(
        (state) => state.statisticsReducer.customer_visits
    );

    const [isLoading, setIsLoading] = useState(false);

    const query = qs.parse(location.search.slice(1));

    const formatDate = (d) =>
        moment.isMoment(d) && d.isValid() ? d.format('YYYY-MM-DD') : null;

    // ===== INIT QUERY =====
    useEffect(() => {
        let q = { ...query };
        let updated = false;

        if (!q.start_date || !q.end_date) {
            q.start_date = moment().startOf('month').format('YYYY-MM-DD');
            q.end_date = moment().endOf('month').format('YYYY-MM-DD');
            updated = true;
        }

        if (updated) {
            history.replace({
                pathname: location.pathname,
                search: qs.stringify(cleanObject(q)),
            });
        }
    }, [location.pathname, location.search]);

    // ===== LOAD DATA =====
    const loadData = async () => {
        const q = qs.parse(location.search.slice(1));

        setIsLoading(true);
        try {
            await dispatch(
                customerVisitStatistics({
                    date_from: q.start_date,
                    date_to: q.end_date,
                })
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!query.start_date || !query.end_date) return;
        loadData();
    }, [location.search]);

    // ===== FILTER =====
    const onChangeFilter = (name, value) => {
        let q = { ...query };

        if (name === 'date') {
            const [start, end] = value || [];
            q.start_date = formatDate(start);
            q.end_date = formatDate(end);
        }

        q = cleanObject(q);

        history.push({
            pathname: location.pathname,
            search: qs.stringify(q),
        });
    };

    // ===== BY GROUP TABLE =====
    const byGroupColumns = [
        {
            title: 'Danh mục dịch vụ',
            dataIndex: 'group_name',
            key: 'group_name',
        },
        {
            title: (
                <span>
                    Lượt đến gắn với dịch vụ{' '}
                    <Tooltip
                        title={
                            <>
                                Một lượt khách đến có thể được tính cho nhiều danh mục dịch vụ
                                <br />
                                nếu khách hàng đang sử dụng nhiều dịch vụ.
                            </>
                        }
                    >
                        <QuestionCircleOutlined
                            style={{ color: '#999', marginLeft: 4 }}
                        />
                    </Tooltip>
                </span>
            ),
            dataIndex: 'count',
            key: 'count',
            align: 'right',
        },
    ];


    return (
        <div>
            <PageTitle routes={routes} title="Thống kê lượt khách đến" />

            <div className="mt-2">
                <Title level={3}>Thống kê lượt khách đến</Title>

                {/* FILTER */}
                <div className="mt-2 mb-2">
                    <Row gutter={16}>
                        <Col xs={24}>
                            <RangePicker
                                format="DD/MM/YYYY"
                                allowClear={false}
                                className="table-button"
                                value={[
                                    query.start_date ? moment(query.start_date) : null,
                                    query.end_date ? moment(query.end_date) : null,
                                ]}
                                onChange={(value) => onChangeFilter('date', value)}
                            />

                            <Button
                                icon={<ReloadOutlined />}
                                className="table-button"
                                loading={isLoading}
                                onClick={loadData}
                            >
                                Refresh
                            </Button>
                        </Col>
                    </Row>
                </div>

                {/* EMPTY */}
                {!summary && !isLoading && (
                    <div style={{ marginTop: 32, textAlign: 'center', opacity: 0.6 }}>
                        <Empty description="Chưa có dữ liệu thống kê" />
                    </div>
                )}

                {/* SUMMARY + BY GROUP */}
                {summary && (
                    <>
                        {/* SUMMARY */}
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            {/* TỔNG LƯỢT KHÁCH ĐẾN */}
                            <Col xs={24} md={8}>
                                <Card style={{ position: 'relative' }}>
                                    <CardHelp
                                        title={
                                            <>
                                                Tổng số lượt khách đến trong khoảng thời gian đã chọn.
                                                <br />
                                                Mỗi lịch hẹn được tính là 1 lượt đến,
                                                <br />
                                                kể cả khi cùng một khách đến nhiều lần.
                                            </>
                                        }
                                    />
                                    <Statistic
                                        title="Tổng lượt khách đến"
                                        value={summary.total_visits}
                                    />
                                </Card>
                            </Col>

                            {/* SỐ KHÁCH DUY NHẤT */}
                            <Col xs={24} md={8}>
                                <Card style={{ position: 'relative' }}>
                                    <CardHelp
                                        title={
                                            <>
                                                Số lượng khách hàng khác nhau đã đến trong khoảng thời gian này.
                                                <br />
                                                Nếu một khách đến nhiều lần,
                                                <br />
                                                vẫn chỉ được tính là 1 khách duy nhất.
                                            </>
                                        }
                                    />
                                    <Statistic
                                        title="Số khách duy nhất"
                                        value={summary.unique_customers}
                                    />
                                </Card>
                            </Col>

                            {/* KHOẢNG THỜI GIAN */}
                            <Col xs={24} md={8}>
                                <Card style={{ position: 'relative' }}>
                                    <CardHelp
                                        title={
                                            <>
                                                Khoảng thời gian dùng để thống kê dữ liệu.
                                                <br />
                                                Chỉ các lịch hẹn đã đến nằm trong khoảng này
                                                <br />
                                                mới được tính vào báo cáo.
                                            </>
                                        }
                                    />
                                    <Statistic
                                        title="Khoảng thời gian"
                                        value={`${isValidDate(summary.date_from, false)} → ${isValidDate(summary.date_to, false)}`}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* BY GROUP TABLE */}
                        <Card title="Thống kê theo danh mục dịch vụ">
                            <Table
                                size="small"
                                rowKey="group_id"
                                columns={byGroupColumns}
                                dataSource={by_group}
                                loading={isLoading}
                                pagination={false}
                            />
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}
